<template>
  <div class="dashboard-container p-5">
    <!-- Header with title and filters -->
    <v-row class="mb-6 align-center">
      <v-col cols="12" md="6">
        <h2 class="title mb-0">System Overview</h2>
      </v-col>
      <v-col
        cols="12"
        md="6"
        class="d-flex justify-end align-center"
        style="gap: 32px;"
      >
        <v-btn-toggle
          v-model="dashboardFilter"
          class="dashboard-filter-toggle"
          @change="onFilterChange"
        >
          <v-btn :value="2" middle>
            Month
          </v-btn>
          <v-btn :value="1" middle>
            Week
          </v-btn>
        </v-btn-toggle>

        <div>
          <label class="date-label">View Date</label>
          <v-menu
            ref="fromDateMenu"
            v-model="fromDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="formattedFromDate"
                readonly
                dense
                outlined
                hide-details
                v-bind="attrs"
                v-on="on"
                style="max-width: 200px;  margin-bottom: 20px; border-radius: 8px;"
                color="#A6CC39"
                placeholder="DD/MM/YYYY"
              >
                <template v-slot:prepend-inner>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style="margin-right: 8px;"
                  >
                    <path
                      d="M19 4H18V2H16V4H8V2H6V4H5C3.89 4 3.01 4.9 3.01 6L3 20C3 21.1 3.89 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM19 20H5V9H19V20ZM7 11H12V16H7V11Z"
                      fill="#A6CC39"
                    />
                  </svg>
                </template>
              </v-text-field>
            </template>
            <v-date-picker
              v-model="fromDate"
              @input="fromDateMenu = false"
              :max="toDate || new Date().toISOString().substr(0, 10)"
            ></v-date-picker>
          </v-menu>
        </div>

        <div>
          <label class="date-label">To Date</label>
          <v-menu
            ref="toDateMenu"
            v-model="toDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="formattedToDate"
                readonly
                dense
                outlined
                hide-details
                v-bind="attrs"
                v-on="on"
                style="max-width: 200px;margin-bottom: 20px; border-radius: 8px;"
                color="#A6CC39"
                placeholder="DD/MM/YYYY"
              >
                <template v-slot:prepend-inner>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style="margin-right: 8px;"
                  >
                    <path
                      d="M19 4H18V2H16V4H8V2H6V4H5C3.89 4 3.01 4.9 3.01 6L3 20C3 21.1 3.89 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM19 20H5V9H19V20ZM7 11H12V16H7V11Z"
                      fill="#A6CC39"
                    />
                  </svg>
                </template>
              </v-text-field>
            </template>
            <v-date-picker
              v-model="toDate"
              @input="toDateMenu = false"
              :min="fromDate"
              :max="new Date().toISOString().substr(0, 10)"
            ></v-date-picker>
          </v-menu>
        </div>

        <v-btn
          color="#6CC8A0"
          @click="applyDateRange"
          :disabled="!fromDate || !toDate"
          style="border-radius: 16px; color: white;"
        >
          Search
        </v-btn>
      </v-col>
    </v-row>

    <!-- Loading Overlay - Disables entire screen -->
    <v-overlay :value="loading" :z-index="9999" opacity="0.7">
      <v-card color="primary" dark width="300">
        <v-card-text>
          Loading...
          <v-progress-linear
            indeterminate
            color="white"
            class="mb-0"
          ></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-overlay>

    <!-- ✅ ROW 1: Companies & System Usage -->
    <v-row class="mb-4">
      <!-- LEFT -->
      <v-col cols="12" lg="5">
        <v-row class="stats-row mb-3" dense>
          <v-col cols="12" sm="6" class="px-2">
            <v-card class="stat-card" height="130" elevation="2">
              <v-card-text class="text-center">
                <h6 class="stat-title">Registered Companies</h6>
                <p class="stat-number">
                  {{ companyList ? companyList.length : 0 }}
                </p>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" class="px-2">
            <v-card class="stat-card" height="130" elevation="2">
              <v-card-text class="text-center">
                <h6 class="stat-title">Total Users</h6>
                <p class="stat-number">{{ Users ? Users.length : 0 }}</p>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row class="stats-row" dense>
          <v-col cols="12" sm="6" class="px-2">
            <v-card class="stat-card" height="130" elevation="2">
              <v-card-text class="text-center">
                <h6 class="stat-title">Active Users</h6>
                <p class="stat-number">{{ ActiveUsers ? ActiveUsers.length : 0 }}</p>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" class="px-2">
            <v-card class="stat-card pie-card" height="130" elevation="2">
              <v-card-text class="donut-flex">
                <div class="donut-canvas mb-3">
                  <canvas
                    ref="activeDonutChart"
                    width="80"
                    height="80"
                  ></canvas>
                </div>
                <div class="donut-legend">
                  <div class="active-indicator">
                    <span class="active-dot"></span>
                    <span> Active {{ activeUser }}%</span>
                  </div>
                  <div class="active-indicator">
                    <span class="inactive-dot-green"></span>
                    <span> Inactive {{ inActiveUser }}%</span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>

      <!-- RIGHT: Growth Line Chart -->
      <v-col cols="12" lg="7">
        <v-card class="chart-card" height="280" elevation="2">
          <v-card-title
            class="chart-title px-4 pt-4 d-flex justify-space-between align-center"
          >
            <span>Growth of Companies and Users</span>
            <v-btn-toggle
              v-model="growthChartFilter"
              class="growth-chart-filter-toggle"
              @change="onGrowthChartFilterChange"
            >
              <v-btn :value="1" small>
                Week
              </v-btn>
              <v-btn :value="2" small>
                Month
              </v-btn>
              <v-btn :value="3" small>
                Year
              </v-btn>
            </v-btn-toggle>
          </v-card-title>
          <v-card-text>
            <canvas ref="growthChart" height="155"></canvas>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- ✅ ROW 2: Companies & System Usage -->
    <v-row class="data-row mb-4">
      <!-- Registered Companies -->
      <v-col cols="12" md="6" lg="4">
        <v-card height="400" elevation="2">
          <v-card-title class="headline">Registered Companies</v-card-title>
          <v-card-text>
            <div class="table-responsive-wrapper">
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th class="registered-id-col">ID</th>
                    <th class="registered-company-col">Company Name</th>
                    <th class="registered-date-col">Registration Date</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(c, i) in topRegisteredCompanies" :key="i">
                    <td class="text-center registered-id-col">C-{{ i + 1 }}</td>
                    <td class="text-center registered-company-col">{{ c.companyName }}</td>
                    <td class="text-center registered-date-col">
                      {{
                        new Date(c.createDate).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric"
                        })
                      }}
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
            <div v-if="!companyList || companyList.length === 0" class="text-center mt-3" style="color: #64748b; font-size: 14px;">
              No data available
            </div>
            <div v-else class="text-right mt-4">
              <div class="more-btn px-5 py-1" @click="dialog = true">
                More
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Dialog for all Registered Companies -->
      <v-dialog v-model="dialog" max-width="700px" :fullscreen="$vuetify.breakpoint.xsOnly">
        <v-card>
          <v-card-title class="headline d-flex justify-space-between align-center">
            <span>All Registered Companies</span>
            <v-btn icon @click="dialog = false" small>
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="px-2 px-sm-4">
            <div class="dialog-table-wrapper">
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th class="dialog-id-col">ID</th>
                    <th class="dialog-company-col">Company Name</th>
                    <th class="dialog-date-col">Registration Date</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(c, i) in companyList" :key="i">
                    <td class="text-center dialog-id-col">C-{{ i + 1 }}</td>
                    <td class="text-center dialog-company-col">{{ c.companyName }}</td>
                    <td class="text-center dialog-date-col">
                      {{
                        new Date(c.createDate).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric"
                        })
                      }}
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </v-card-text>
          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn text @click="dialog = false" class="more-btn px-5 py-1"
              >Close</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Expiring Companies -->
      <v-col cols="12" md="6" lg="4">
        <v-card height="400" elevation="2">
          <v-card-title class="headline "
            >Expiring Companie</v-card-title
          >
          <v-card-text class="px-4">
            <div class="table-responsive-wrapper">
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th class="expiring-id-col">ID</th>
                    <th class="expiring-company-col">Company Name</th>
                    <th class="expiring-date-col">Expiration Date</th>
                    <th class="expiring-status-col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(c, i) in topExpiredCompany" :key="i">
                    <td class="text-center expiring-id-col">C-{{ i + 1 }}</td>
                    <td class="text-center expiring-company-col">{{ c.companyName }}</td>
                    <td class="text-center expiring-date-col">
                      {{
                        new Date(c.expiryDate).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric"
                        })
                      }}
                    </td>
                    <td class="text-center expiring-status-col">
                      <span
                        class="status-badge"
                        :style="{
                          color: getStatusColor(getDaysLeft(c.expiryDate)),
                          backgroundColor: getbgColor(getDaysLeft(c.expiryDate))
                        }"
                      >
                        {{ getStatusText(getDaysLeft(c.expiryDate)) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
            <div v-if="!companyList || companyList.length === 0" class="text-center mt-3" style="color: #64748b; font-size: 14px;">
              No data available
            </div>
            <div v-else class="text-right mt-4">
              <div
                class="more-btn px-5 py-1"
                @click="dialogExpire = true"
              >
                More
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Dialog for all Expiring Companies -->
      <v-dialog v-model="dialogExpire" max-width="800px" :fullscreen="$vuetify.breakpoint.xsOnly">
        <v-card>
          <v-card-title class="headline d-flex justify-space-between align-center">
            <span>All Expiring Companies</span>
            <v-btn icon @click="dialogExpire = false" small>
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="px-2 px-sm-4">
            <div class="dialog-table-wrapper">
              <v-simple-table dense>
                <thead>
                  <tr>
                    <th class="dialog-id-col">ID</th>
                    <th class="dialog-company-col">Company Name</th>
                    <th class="dialog-date-col">Expiration Date</th>
                    <th class="dialog-status-col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(c, i) in companyList" :key="i">
                    <td class="text-center dialog-id-col">C-{{ i + 1 }}</td>
                    <td class="text-center dialog-company-col">{{ c.companyName }}</td>
                    <td class="text-center dialog-date-col">
                      {{
                        new Date(c.expiryDate).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric"
                        })
                      }}
                    </td>
                    <td class="text-center dialog-status-col">
                      <span
                        class="status-badge"
                        :style="{
                          color: getStatusColor(getDaysLeft(c.expiryDate)),
                          backgroundColor: getbgColor(getDaysLeft(c.expiryDate))
                        }"
                      >
                        {{ getStatusText(getDaysLeft(c.expiryDate)) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </div>
          </v-card-text>
          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn text @click="dialogExpire = false" class="more-btn px-5 py-1"
              >Close</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ✅ System Usage -->
      <v-col cols="12" md="6" lg="4">
        <v-card height="400" elevation="2">
          <v-card-title class="headline px-4 pt-4">
            System Usage & Performance
          </v-card-title>
          <div class="d-flex px-4 mb-3" style="gap:10px;">
            <v-select
              v-model="selectedTimePeriod"
              :items="timePeriods"
              item-text="text"
              item-value="value"
              label="Time Period"
              dense
              outlined
              hide-details
              @change="fetchLoginActivity"
            ></v-select>
            <v-select
              v-model="selectedPlatform"
              :items="platforms"
              item-text="text"
              item-value="value"
              label="Platform"
              dense
              outlined
              hide-details
              @change="fetchLoginActivity"
            ></v-select>
          </div>
          <v-card-text class="px-4">
            <canvas ref="usageChart" height="220"></canvas>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- ✅ ROW 3: Final Section -->
    <v-row class="bottom-row mb-4">
      <!-- User Management -->
      <v-col cols="12" md="6" lg="4">
        <v-card height="380" elevation="2">
          <v-card-title class="headline px-4 pt-4"
            >User Management</v-card-title
          >
          <v-card-text class="px-4">
            <div
              v-for="(user, i) in top7mostUserCount"
              :key="i"
              class="progress my-4"
            >
              <span>{{ user.companyName }}</span>
              <div class="bar">
                <div
                  class="fill"
                  :style="{ width: user.userCount + '%' }"
                ></div>
              </div>
              <span class="value">{{ user.userCount }}</span>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Top 7 Content Producers -->
      <v-col cols="12" md="6" lg="4">
        <v-card height="380" elevation="2">
          <v-card-title class="headline px-4 pt-4"
            >Top 7 Content Producers</v-card-title
          >
          <v-card-text class="px-4">
            <v-simple-table dense>
              <thead>
                <tr>
                  <th></th>
                  <th>Company</th>
                  <th>Items</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(item, index) in topProducers"
                  :key="index"
                  :style="{
                    backgroundColor: index === 1 ? '#F4FDF0' : '#FFFBDE',
                    color: '#000',
                    fontWeight: '500'
                  }"
                >
                  <td class="text-center">{{ index + 1 }}</td>
                  <td class="text-center">{{ item.name }}</td>
                  <td class="text-center">{{ item.value }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Content Overview Donut -->
      <v-col cols="12" md="6" lg="4">
        <v-card height="380" elevation="2">
          <v-card-title class="headline px-4 pt-4"
            >Content Overview</v-card-title
          >
          <v-card-text class="px-4">
            <div class="donut-canvas-large">
              <canvas ref="contentDonutChart" width="200" height="200"></canvas>
            </div>
            <div class="labels-grid">
              <div
                v-for="(item, i) in contentData.labels"
                :key="i"
                class="label-item"
              >
                <span
                  class="color-dot"
                  :style="{ backgroundColor: contentData.colors[i] }"
                ></span>
                <span>{{ item }}</span>
              </div>
            </div>

            <div class="updated-text">Updated just now</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import axios from "axios";
import Chart from "chart.js";

export default {
  name: "MenuDashboard",
  data() {
    return {
      months: [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec"
      ],

      timePeriods: [
        { text: "Week", value: 1 },
        { text: "Month", value: 2 },
        { text: "Year", value: 3 }
      ],
      platforms: [
        { text: "Both", value: 3 },
        { text: "Web", value: 1 },
        { text: "App", value: 2 }
      ],

      selectedTimePeriod: 1,
      selectedPlatform: 3,
      dashboardFilter: 2,
      growthChartFilter: 2, // Default to Month
      fromDate: null,
      toDate: null,
      fromDateMenu: false,
      toDateMenu: false,
      loginActivityData: [],
      usersManagement: [],
      topProducers: [
        { name: "GreenTech", value: 191 },
        { name: "AlphaInnovations", value: 290 },
        { name: "Gamma LLC", value: 275 },
        { name: "Delta Solutions", value: 260 },
        { name: "Epsilon Ltd.", value: 245 },
        { name: "Zeta Group", value: 230 },
        { name: "Clean Energy", value: 210 }
      ],
      contentData: {
        labels: [
          "Videos",
          "Articles",
          "Sound",
          "Lesson Learn",
          "Quiz",
          "Event"
        ],
        percentage: [],
        count: [],
        colors: [
          "#64B5F6",
          "#81C784",
          "#FFD54F",
          "#BA68C8",
          "#E57373",
          "#4DD0E1"
        ]
      },

      url: "https://devseedapi.azurewebsites.net/api/v1/",
      companyList: [],
      Users: [],
      ActiveUsers: [],
      activeUser: 0,
      inActiveUser: 0,
      activeStats: [],
      activeDonut: null,
      growthChart: null,
      companyGrowth: [],
      userGrowth: [],
      dialog: false,
      dialogExpire: false,
      contentDonut: null,
      usageChart: null,
      customerWeb: [],
      App: [],
      userView: [],
      loading: false
    };
  },

  computed: {
    topRegisteredCompanies() {
      return this.companyList ? this.companyList.slice(0, 7) : [];
    },
    topExpiredCompany() {
      return this.companyList ? this.companyList.slice(0, 7) : [];
    },
    top7mostUserCount() {
      return this.usersManagement ? this.usersManagement.slice(0, 7) : [];
    },
    formattedFromDate() {
      return this.fromDate ? this.formatDateDisplay(this.fromDate) : '';
    },
    formattedToDate() {
      return this.toDate ? this.formatDateDisplay(this.toDate) : '';
    }
  },

  methods: {
    async loadDashboardData(fromDate = null, toDate = null) {
      await Promise.all([
        this.GetRegisteredCompany(fromDate, toDate),
        this.GetAllUsers(fromDate, toDate),
        this.GetActiveUsers(fromDate, toDate),
        this.GetActiveInActiveUsersPercentage(fromDate, toDate)
      ]);
    },

    async onFilterChange() {
      if (this.dashboardFilter === null) return;
      // Set date range based on filter selection
      this.setDateRangeForFilter(this.dashboardFilter);
      this.loading = true;
      try {
        // Reload all dashboard data with the selected filter
        await this.loadDashboardData(this.fromDate, this.toDate);
        await this.GetUsersManagement(this.fromDate, this.toDate);
        await this.GetContentOverviewPercentage(this.fromDate, this.toDate);
      } finally {
        this.loading = false;
      }
    },

    setDateRangeForFilter(filterType) {
      const today = new Date();
      let fromDate, toDate;

      if (filterType === 1) {
        // Week: Current week's Monday to Sunday
        const currentDay = today.getDay();
        const daysToMonday = currentDay === 0 ? 6 : currentDay - 1; // If Sunday, go back 6 days
        
        fromDate = new Date(today);
        fromDate.setDate(today.getDate() - daysToMonday);
        fromDate.setHours(0, 0, 0, 0); // Start of Monday
        
        toDate = new Date(fromDate);
        toDate.setDate(fromDate.getDate() + 6); // Sunday
        toDate.setHours(23, 59, 59, 999); // End of Sunday
      } else if (filterType === 2) {
        // Month: First day to last day of current month
        fromDate = new Date(today.getFullYear(), today.getMonth(), 1);
        fromDate.setHours(0, 0, 0, 0); // Start of first day
        
        toDate = new Date(today.getFullYear(), today.getMonth() + 1, 0); // Last day of current month
        toDate.setHours(23, 59, 59, 999); // End of last day
      }

      this.fromDate = this.formatDateForPicker(fromDate);
      this.toDate = this.formatDateForPicker(toDate);
    },

    formatDateForPicker(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    formatDateDisplay(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    },

    async applyDateRange() {
      if (!this.fromDate || !this.toDate) {
        alert("Please select both From Date and To Date");
        return;
      }
      // Clear the toggle selection when using custom date range
      this.dashboardFilter = null;
      this.loading = true;
      try {
        // Reload all dashboard data with custom date range
        await this.loadDashboardData(this.fromDate, this.toDate);
        await this.GetUsersManagement(this.fromDate, this.toDate);
        await this.GetContentOverviewPercentage(this.fromDate, this.toDate);
      } finally {
        this.loading = false;
      }
    },

    getDaysLeft(expiryDate) {
      const today = new Date();
      const expire = new Date(expiryDate);
      const diffTime = expire - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    },
    getStatusColor(daysLeft) {
      if (daysLeft < 0) return "red";
      if (daysLeft <= 7) return "orange";
      return "green";
    },
    getbgColor(daysLeft) {
      if (daysLeft < 0) return "#fee2e2";
      if (daysLeft <= 7) return "#ffedd5";
      return "#dcfce7";
    },
    getStatusText(daysLeft) {
      if (daysLeft < 0) return "Expired";
      return `${daysLeft} days left`;
    },
    async GetRegisteredCompany(fromDate = null, toDate = null) {
      const params = {};
      if (fromDate && toDate) {
        params.fromDate = fromDate;
        params.toDate = toDate;
      }
      try {
        const response = await axios.get(
          `${this.url}DashboardView/GetAllRegisteredCompany`,
          {
            params
          }
        );
        this.companyList = response.data.data || [];

        const today = new Date();

        // Sort: active first (earliest expiry first), expired at bottom
        this.companyList.sort((a, b) => {
          const dateA = new Date(a.expiryDate);
          const dateB = new Date(b.expiryDate);

          const isExpiredA = dateA < today;
          const isExpiredB = dateB < today;

          // Push expired to bottom
          if (isExpiredA && !isExpiredB) return 1;
          if (!isExpiredA && isExpiredB) return -1;

          // Both expired or both active → sort ascending by date
          return dateA - dateB;
        });

        // Chart logic - use growth chart filter
        if (this.growthChartFilter === 1) {
          this.companyGrowth = this.countByWeek(this.companyList, "createDate");
        } else if (this.growthChartFilter === 3) {
          this.companyGrowth = this.countByYear(this.companyList, "createDate");
        } else {
          this.companyGrowth = this.countByMonth(
            this.companyList,
            "createDate"
          );
        }
        this.renderGrowthChart();
      } catch (error) {
        console.error("Error fetching companies:", error);
        this.companyList = [];
        this.companyGrowth = [];
      }
    },

    async GetUsersManagement(fromDate = null, toDate = null) {
      const params = {};
      if (fromDate && toDate) {
        params.fromDate = fromDate;
        params.toDate = toDate;
      }
      try {
        const response = await axios.get(
          `${this.url}DashboardView/GetAllRegisteredCompany`,
          {
            params
          }
        );
        console.log("✅ User Management Response:", response);

        // Bind user management data
        this.usersManagement = response.data.data || response.data || [];
        console.log(" usersManagement:", this.usersManagement);
      } catch (error) {
        console.error(" API error:", error);
        this.usersManagement = [];
      }
    },

    async GetAllUsers(fromDate = null, toDate = null) {
      const params = {};
      if (fromDate && toDate) {
        params.fromDate = fromDate;
        params.toDate = toDate;
      }
      try {
        const response = await axios.get(
          `${this.url}DashboardView/GetAllUsers`,
          {
            params
          }
        );
        this.Users = response.data.data || [];
        if (this.growthChartFilter === 1) {
          this.userGrowth = this.countByWeek(this.Users, "createDate");
        } else if (this.growthChartFilter === 3) {
          this.userGrowth = this.countByYear(this.Users, "createDate");
        } else {
          this.userGrowth = this.countByMonth(this.Users, "createDate");
        }
        this.renderGrowthChart();
      } catch (error) {
        console.error("Error fetching users:", error);
        this.Users = [];
        this.userGrowth = [];
      }
    },
    async GetActiveUsers(fromDate = null, toDate = null) {
      const params = {};
      if (fromDate && toDate) {
        params.fromDate = fromDate;
        params.toDate = toDate;
      }
      try {
        const response = await axios.get(
          `${this.url}DashboardView/GetActiveUsers`,
          {
            params
          }
        );
        this.ActiveUsers = response.data.data || [];
        // GetActiveUsers should not update chart data - that's handled by GetAllUsers
      } catch (error) {
        console.error("Error fetching active users:", error);
        this.ActiveUsers = [];
      }
    },
    async GetActiveInActiveUsersPercentage(
      fromDate = null,
      toDate = null
    ) {
      const params = {};
      if (fromDate && toDate) {
        params.fromDate = fromDate;
        params.toDate = toDate;
      }
      try {
        const response = await axios.get(
          `${this.url}DashboardView/GetActiveInActiveUsersPercentage`,
          {
            params
          }
        );
        if (response.data && response.data.data) {
          this.activeUser = response.data.data.activeUser;
          this.inActiveUser = response.data.data.inActiveUser;
          this.activeStats = [this.activeUser, this.inActiveUser];
          this.renderActiveDonut();
        }
      } catch (error) {
        console.error("Error fetching active/inactive percentage:", error);
        this.activeUser = 0;
        this.inActiveUser = 0;
        this.activeStats = [0, 0];
      }
    },
    async GetContentOverviewPercentage(
      fromDate = null,
      toDate = null
    ) {
      const params = {};
      if (fromDate && toDate) {
        params.fromDate = fromDate;
        params.toDate = toDate;
      }
      try {
        const response = await axios.get(
          `${this.url}DashboardView/GetAllContentOverview`,
          {
            params
          }
        );
        const d = response?.data?.data;
        if (!d) return;

        this.contentData.percentage = [
          d.videoCountPercentage || 0,
          d.articleCountPercentage || 0,
          d.soundCountPercentage || 0,
          d.lessonCountPercentage || 0,
          d.quizCountPercentage || 0,
          d.eventCountPercentage || 0
        ];

        this.contentData.count = [
          d.videoCount || 0,
          d.articleCount || 0,
          d.soundCount || 0,
          d.lessonCount || 0,
          d.quizCount || 0,
          d.eventCount || 0
        ];

        this.renderContentDonut();
      } catch (error) {
        console.log(error);
      }
    },

    async fetchLoginActivity() {
      try {
        const response = await axios.get(
          `${this.url}DashboardView/GetLoginActivity`,
          {
            params: {
              dateType: this.selectedTimePeriod,
              platformType: this.selectedPlatform
            }
          }
        );

        if (response.data.status === 0) {
          this.loginActivityData = response.data.data || [];
          this.renderUsageChart();
        }
      } catch (error) {
        console.error("Error fetching login activity:", error);
      }
    },
    countByMonth(list, dateKey) {
      const monthlyCounts = Array(12).fill(0);
      if (!list || !Array.isArray(list)) return monthlyCounts;
      list.forEach(item => {
        if (item[dateKey]) {
          const month = new Date(item[dateKey]).getMonth();
          if (!isNaN(month)) monthlyCounts[month]++;
        }
      });
      return monthlyCounts;
    },

    countByWeek(list, dateKey) {
      // Get current week and previous 6 weeks (7 weeks total) - Monday to Sunday
      const weekCounts = Array(7).fill(0);
      if (!list || !Array.isArray(list)) return weekCounts;
      const now = new Date();

      // Get Monday of current week
      const currentMonday = new Date(now);
      const currentDayOfWeek = now.getDay();
      const daysToMonday = currentDayOfWeek === 0 ? 6 : currentDayOfWeek - 1; // If Sunday, go back 6 days
      currentMonday.setDate(now.getDate() - daysToMonday);

      list.forEach(item => {
        if (item[dateKey]) {
          const itemDate = new Date(item[dateKey]);
          const itemDay = itemDate.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

          // Count all days of the week (Monday to Sunday)
          // Find which week this item belongs to
          for (let i = 0; i < 7; i++) {
            const weekMonday = new Date(currentMonday);
            weekMonday.setDate(currentMonday.getDate() - i * 7);

            const weekSunday = new Date(weekMonday);
            weekSunday.setDate(weekMonday.getDate() + 6);

            if (itemDate >= weekMonday && itemDate <= weekSunday) {
              weekCounts[6 - i]++; // Reverse order for chronological display
              break;
            }
          }
        }
      });
      return weekCounts;
    },

    countByYear(list, dateKey) {
      // Get current year and previous 4 years (5 years total)
      const yearCounts = Array(5).fill(0);
      if (!list || !Array.isArray(list)) return yearCounts;
      const currentYear = new Date().getFullYear();

      list.forEach(item => {
        if (item[dateKey]) {
          const itemYear = new Date(item[dateKey]).getFullYear();
          const yearDiff = currentYear - itemYear;
          if (yearDiff >= 0 && yearDiff < 5) {
            yearCounts[4 - yearDiff]++; // Reverse order for chronological display
          }
        }
      });
      return yearCounts;
    },

    getGrowthChartLabels() {
      if (this.growthChartFilter === 1) {
        // Weekday labels (Monday to Sunday with date range)
        const labels = [];
        const now = new Date();

        // Get Monday of current week
        const currentMonday = new Date(now);
        const currentDayOfWeek = now.getDay();
        const daysToMonday = currentDayOfWeek === 0 ? 6 : currentDayOfWeek - 1; // If Sunday, go back 6 days
        currentMonday.setDate(now.getDate() - daysToMonday);

        for (let i = 6; i >= 0; i--) {
          // Calculate Monday for each week going backwards
          const weekMonday = new Date(currentMonday);
          weekMonday.setDate(currentMonday.getDate() - i * 7);

          // Get Sunday of that week (6 days after Monday)
          const weekSunday = new Date(weekMonday);
          weekSunday.setDate(weekMonday.getDate() + 6);

          // Format: "24-30 Nov"
          const mondayDay = weekMonday.getDate();
          const sundayDay = weekSunday.getDate();

          // Handle month transitions
          const mondayMonth = weekMonday.toLocaleDateString("en-GB", {
            month: "short"
          });
          const sundayMonth = weekSunday.toLocaleDateString("en-GB", {
            month: "short"
          });

          if (mondayMonth === sundayMonth) {
            labels.push(`${mondayDay}-${sundayDay} ${mondayMonth}`);
          } else {
            labels.push(
              `${mondayDay} ${mondayMonth}-${sundayDay} ${sundayMonth}`
            );
          }
        }
        return labels;
      } else if (this.growthChartFilter === 3) {
        // Year labels
        const labels = [];
        const currentYear = new Date().getFullYear();
        for (let i = 4; i >= 0; i--) {
          labels.push((currentYear - i).toString());
        }
        return labels;
      } else {
        // Month labels (default)
        return this.months;
      }
    },

    onGrowthChartFilterChange() {
      if (this.growthChartFilter === null) return;

      // Update growth data based on selected filter without loading
      this.updateGrowthData();
    },

    updateGrowthData() {
      // Update growth data based on filter without API calls - use existing data
      if (this.growthChartFilter === 1) {
        this.companyGrowth = this.countByWeek(this.companyList, "createDate");
        this.userGrowth = this.countByWeek(this.Users, "createDate");
      } else if (this.growthChartFilter === 3) {
        this.companyGrowth = this.countByYear(this.companyList, "createDate");
        this.userGrowth = this.countByYear(this.Users, "createDate");
      } else {
        this.companyGrowth = this.countByMonth(this.companyList, "createDate");
        this.userGrowth = this.countByMonth(this.Users, "createDate");
      }

      this.renderGrowthChart();
    },

    renderActiveDonut() {
      this.$nextTick(() => {
        if (!this.$refs.activeDonutChart) {
          console.warn("Active donut chart canvas not found");
          return;
        }

        if (this.activeDonut) this.activeDonut.destroy();

        const self = this;
        this.activeDonut = new Chart(this.$refs.activeDonutChart, {
          type: "doughnut",
          data: {
            labels: ["Active", "Inactive"],
            datasets: [
              {
                data: this.activeStats,
                backgroundColor: ["#A6CC39", "#4CAF50"],
                borderWidth: 0
              }
            ]
          },
          options: {
            cutoutPercentage: 70,
            legend: {
              display: false
            },
            tooltips: {
              enabled: true,
              bodyFontSize: 8,
              xPadding: 8,
              yPadding: 6,
              cornerRadius: 4,
              callbacks: {
                title: function() {
                  return '';
                },
                label: function(tooltipItem, data) {
                  const labels = ["Active", "Inactive"];
                  const value = self.activeStats[tooltipItem.index];
                  return `${labels[tooltipItem.index]}: ${value}%`;
                }
              }
            },
            responsive: true,
            maintainAspectRatio: true
          }
        });
      });
    },

    renderGrowthChart() {
      this.$nextTick(() => {
        if (!this.$refs.growthChart) return;
        
        const labels = this.getGrowthChartLabels();

        if (!this.growthChart) {
        this.growthChart = new Chart(this.$refs.growthChart, {
          type: "line",
          data: {
            labels: labels,
            datasets: [
              {
                label: "Companies",
                data: this.companyGrowth,
                borderColor: "#A6CC39",
                backgroundColor: "rgba(166, 204, 57, 0.1)",
                fill: true,
                tension: 0.3,
                pointRadius: 4
              },
              {
                label: "Users",
                data: this.userGrowth,
                borderColor: "#42A5F5",
                backgroundColor: "rgba(66, 165, 245, 0.1)",
                fill: true,
                tension: 0.3,
                pointRadius: 4
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: "top",
                labels: { usePointStyle: true, padding: 10 }
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: { stepSize: 5 },
                grid: { color: "#e0e0e0" }
              },
              x: { grid: { color: "#f5f5f5" } }
            }
          }
        });
      } else {
        this.growthChart.data.labels = labels;
        this.growthChart.data.datasets[0].data = this.companyGrowth;
        this.growthChart.data.datasets[1].data = this.userGrowth;
        this.growthChart.update();
      }
      });
    },

    getUsageData() {
      axios
        .all([
          axios.get(this.url + "DashboardView/GetLoginCustomerWebApp"),
          axios.get(this.url + "DashboardView/GetLoginMobile"),
          axios.get(this.url + "DashboardView/GetLoginUserView")
        ])
        .then(
          axios.spread((webRes, appRes, userRes) => {
            const web = webRes.data.data.result || [];
            const app = appRes.data.data.result || [];
            const user = userRes.data.data.result || [];

            this.webData = this.countByMonth(web, "createDate");
            this.appData = this.countByMonth(app, "createDate");
            this.userData = this.countByMonth(user, "createDate");

            this.renderUsageChart();
          })
        )
        .catch(error => console.log(error));
    },
    renderUsageChart() {
      this.$nextTick(() => {
        if (!this.$refs.usageChart) return;
        if (this.usageChart) {
          this.usageChart.destroy();
        }

        // Ensure loginActivityData is always an array
        const loginActivityData = Array.isArray(this.loginActivityData) ? this.loginActivityData : [];
        if (!loginActivityData.length) return;

        // For year view (timeType = 3), show month names; otherwise show day + month
        const labels = loginActivityData.map(item => {
        const date = new Date(item.createDate);
        if (this.selectedTimePeriod === 3) {
          // Year view: show month names (Jan, Feb, Mar, etc.)
          return date.toLocaleDateString("en-GB", {
            month: "short"
          });
        } else {
          // Week/Month view: show day + month
          return date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short"
          });
        }
      });

      const datasets = [];

      if (this.selectedPlatform === 1 || this.selectedPlatform === 3) {
        datasets.push({
          label: "Web Logins",
          data: loginActivityData.map(item => item.webLoginCount),
          borderColor: "#64B5F6",
          backgroundColor: "rgba(100,181,246,0.1)",
          tension: 0.4,
          fill: true
        });
      }

      if (this.selectedPlatform === 2 || this.selectedPlatform === 3) {
        datasets.push({
          label: "App Logins",
          data: loginActivityData.map(item => item.appLoginCount),
          borderColor: "#81C784",
          backgroundColor: "rgba(129,199,132,0.1)",
          tension: 0.4,
          fill: true
        });
      }

      datasets.push({
        label: "Total Logins",
        data: loginActivityData.map(item => item.totalLoginCount),
        borderColor: "#FFD54F",
        backgroundColor: "rgba(255,213,79,0.1)",
        tension: 0.4,
        fill: true
      });

      this.usageChart = new Chart(this.$refs.usageChart, {
        type: "line",
        data: {
          labels: labels,
          datasets: datasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: "top",
              labels: { usePointStyle: true, padding: 15 }
            },
            tooltip: {
              callbacks: {
                afterLabel: context => {
                  const index = context.dataIndex;
                  const data = loginActivityData[index];
                  return [
                    `Companies: ${data.companyCount}`,
                    `Users: ${data.userCount}`
                  ];
                }
              }
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: { stepSize: 10 }
            }
          }
        }
        });
      });
    },
    renderContentDonut() {
      this.$nextTick(() => {
        if (!this.$refs.contentDonutChart) return;
        if (this.contentDonut) this.contentDonut.destroy();

        // Ensure contentData.percentage is always an array
        const percentage = Array.isArray(this.contentData.percentage) ? this.contentData.percentage : [];
        if (!percentage.length) return;

        const self = this;
        this.contentDonut = new Chart(this.$refs.contentDonutChart, {
        type: "doughnut",
        data: {
          labels: this.contentData.labels,
          datasets: [
            {
              data: percentage,
              backgroundColor: this.contentData.colors,
              borderWidth: 0
            }
          ]
        },
        options: {
          cutoutPercentage: 60,
          legend: {
            display: false
          },
          tooltips: {
            enabled: true,
            padding: 12,
              cornerRadius: 6,
              bodyFontSize: 11,
            callbacks: {
              title: function() {
                return '';
              },
              label: function(tooltipItem, data) {
                const i = tooltipItem.index;
                const label = self.contentData.labels[i] || "Unknown";
                const count = self.contentData.count[i] || 0;
                const percentageVal = percentage[i] || 0;
                return `${label}: ${count} (${percentageVal.toFixed(2)}%)`;
              }
            }
          },
          responsive: true,
          maintainAspectRatio: true
        }
      });
      });
    }
  },
  mounted() {
   
    this.setDateRangeForFilter(this.dashboardFilter);
    this.loadDashboardData(this.fromDate, this.toDate);
    this.GetUsersManagement(this.fromDate, this.toDate);
    this.GetContentOverviewPercentage(this.fromDate, this.toDate);
    this.fetchLoginActivity();
  }
};
</script>

<style scoped>
.dashboard-container {
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
  min-height: 100vh;
}
.title {
  font-size: 28px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 20px;
  text-align: left;
}

/* Responsive Typography */
@media (max-width: 960px) {
  .title {
    font-size: 24px;
  }
}

@media (max-width: 600px) {
  .title {
    font-size: 20px;
    text-align: center;
  }
}
.stat-card {
  background-color: #e3ffe5 !important;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
  width: 100%;
}
.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
}
.stat-title {
  font-size: 13px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
}
.stat-number {
  font-size: 32px;
  font-weight: 700;
  color: #000000;
  margin: 0;
}

@media (max-width: 600px) {
  .stat-number {
    font-size: 28px;
  }
  .stat-title {
    font-size: 12px;
  }
}
.pie-card {
  background: #fffbe5 !important;
}
.donut-flex {
  display: flex;
  align-items: center;
  justify-content: space-evenly;
}
.donut-legend {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.85rem;
}
.active-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
}
.active-dot,
.inactive-dot,
.inactive-dot-green {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}
.active-dot {
  background-color: #a6cc39;
}
.inactive-dot {
  background-color: #ffd18b;
}
.inactive-dot-green {
  background-color: #418D4A;
}
.chart-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.2s;
}
.chart-card:hover {
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
}
.status-cell {
  color: rgba(255, 255, 255, 0.845);
  border-radius: 8px;
  text-align: center;
}
.warning {
  background-color: #ffefc5e1 !important;
  width: 100px;
}
.success {
  background-color: #b8ffbc;
}
.expired {
  background-color: #ffb3b3df;
}
.warning-text {
  background-color: #ffff !important;
  width: 100px;
}
.text-green {
  color: rgb(17, 186, 8) !important;
}
.text-red {
  color: rgb(186, 8, 8) !important;
}
.chart-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #2c3e50;
  padding-bottom: 12px;
}

.headline {
  font-size: 1.1rem !important;
  font-weight: 700;
  color: #2c3e50;
  padding-bottom: 12px;
}

@media (max-width: 600px) {
  .headline {
    font-size: 1rem !important;
  }
  .chart-title {
    font-size: 1rem;
  }
}

.progress {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  font-weight: 500;
  height: 24px;
  color: #2c3e50;
}
.bar {
  flex: 1;
  height: 18px;
  background-color: #e8ecf1;
  border-radius: 8px;
  margin: 0 12px;
  overflow: hidden;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.06);
}
.fill {
  height: 100%;
  background: linear-gradient(90deg, #a6cc39 0%, #8fb82e 100%);
  border-radius: 8px;
  transition: width 0.5s ease;
  box-shadow: 0 2px 4px rgba(166, 204, 57, 0.3);
}

.value {
  min-width: 30px;
  text-align: right;
  font-size: 14px;
  font-weight: 600;
  color: #a6cc39;
}

.donut-canvas-large {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 15px;
  max-width: 200px;
  max-height: 200px;
  margin-left: auto;
  margin-right: auto;
}
.more-btn {
  background: #E5E5E56B;
  border-radius: 8px;
  width: 71px;
  height: 24px;
  cursor: pointer;
  font-weight: 600;
  font-size: 12px;
  color: black;
  transition: transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: inline-block;
  text-align: center;
  padding-left: 8px;
  padding-right: 8px;
  gap: 10px;
}
.more-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.labels-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px 10px;
  margin-top: 20px;
}
.label-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  color: #2c3e50;
}
.color-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}
.updated-text {
  display: flex;
  justify-content: flex-end;
  padding-top: 15px;
  font-size: 0.85rem;
  font-style: italic;
  color: #64748b;
}
.bottom-row {
  margin-top: 20px;
}

/* Ensure canvas elements are properly sized */
.donut-canvas {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  flex-shrink: 0;
}

.donut-canvas canvas {
  display: block;
  max-width: 100%;
  max-height: 100%;
}

/* Table styling */
.v-data-table th {
  font-weight: 700 !important;
  color: #2c3e50 !important;
  background-color: #f8fafc !important;
}

.v-data-table td {
  color: #475569 !important;
}

/* Card text adjustments */
.v-card-text {
  padding-top: 12px;
}

/* Smooth scrollbar for dialogs */
.v-card-text div::-webkit-scrollbar {
  width: 8px;
}

.v-card-text div::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

.v-card-text div::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.v-card-text div::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Table row hover effect */
.v-simple-table tbody tr:hover {
  background-color: #f8fafc !important;
}

/* Data row spacing */
.data-row {
  margin-top: 16px;
}

/* Stats row spacing */
.stats-row {
  margin-bottom: 8px;
}

/* Dashboard filter toggle */
.dashboard-filter-toggle {
  border: none;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: none;
}

.dashboard-filter-toggle .v-btn {
  text-transform: none;
  font-weight: 600;
  color: #64748b;
  background-color: #ffffff;
  min-width: 70px;
  width: 70px;
  height: 45px;
  font-size: 12px;
  font-weight: 600;
  border: none;
  outline: none;
}

.dashboard-filter-toggle .v-btn:first-child {
  border-radius: 16px 0 0 16px;
}

.dashboard-filter-toggle .v-btn:last-child {
  border-radius: 0 16px 16px 0;
}

.dashboard-filter-toggle .v-btn--active {
  background: #def7e3 !important;
  color: #2c2c2c;
  font-weight: 700;
}

.dashboard-filter-toggle .v-btn:hover {
  background-color: #f8fafc;
}

/* Growth Chart Filter Toggle */
.growth-chart-filter-toggle {
  border: none;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: none;
}

.growth-chart-filter-toggle .v-btn {
  text-transform: none;
  font-weight: 600;
  color: #64748b;
  background-color: #ffffff;
  min-width: 50px;
  width: 50px;
  height: 32px;
  font-size: 11px;
  font-weight: 600;
  border: none;
  outline: none;
}

.growth-chart-filter-toggle .v-btn:first-child {
  border-radius: 12px 0 0 12px;
}

.growth-chart-filter-toggle .v-btn:last-child {
  border-radius: 0 12px 12px 0;
}

.growth-chart-filter-toggle .v-btn--active {
  background: #def7e3 !important;
  color: #2c2c2c;
  font-weight: 700;
}

.growth-chart-filter-toggle .v-btn:hover {
  background-color: #f8fafc;
}

/* Mobile Responsive Adjustments */
@media (max-width: 600px) {
  .dashboard-container {
    padding: 16px !important;
  }

  .v-card {
    margin-bottom: 16px;
  }

  .labels-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .donut-flex {
    flex-direction: column;
    gap: 12px;
  }

  .more-btn {
    width: 80px;
    font-size: 0.875rem;
  }
}

/* Tablet Responsive Adjustments */
@media (max-width: 960px) and (min-width: 601px) {
  .labels-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* Date Label Styling */
.date-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
}

/* Calendar Icon Color */
.v-text-field .v-icon.mdi-calendar {
  color: #a6cc39 !important;
}

.v-text-field--outlined .v-icon.mdi-calendar,
.v-text-field.v-input--is-focused .v-icon.mdi-calendar,
.v-text-field.v-input--is-focused:not(.v-input--is-disabled)
  .v-icon.mdi-calendar,
.v-text-field:hover .v-icon.mdi-calendar,
.v-text-field.v-input--is-dirty .v-icon.mdi-calendar,
.v-input--is-focused .v-icon.mdi-calendar {
  color: #a6cc39 !important;
}

/* Table Responsive Wrapper */
.table-responsive-wrapper {
  overflow-x: auto;
  width: 100%;
  -webkit-overflow-scrolling: touch;
}

.table-responsive-wrapper::-webkit-scrollbar {
  height: 6px;
}

.table-responsive-wrapper::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

.table-responsive-wrapper::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.table-responsive-wrapper::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Expiring Companies Table Columns */
.expiring-id-col {
  min-width: 50px;
  width: 60px;
  white-space: nowrap;
}

.expiring-company-col {
  min-width: 120px;
  max-width: 180px;
  word-wrap: break-word;
  word-break: break-word;
  white-space: normal;
}

.expiring-date-col {
  min-width: 100px;
  width: 110px;
  white-space: nowrap;
}

.expiring-status-col {
  min-width: 90px;
  width: 100px;
}

/* Registered Companies Table Columns */
.registered-id-col {
  min-width: 50px;
  width: 60px;
  white-space: nowrap;
}

.registered-company-col {
  min-width: 120px;
  max-width: 180px;
  word-wrap: break-word;
  word-break: break-word;
  white-space: normal;
}

.registered-date-col {
  min-width: 100px;
  width: 120px;
  white-space: nowrap;
}

/* Status Badge */
.status-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.85rem;
  font-weight: 500;
  display: inline-block;
  white-space: nowrap;
}

/* Dialog Table Wrapper */
.dialog-table-wrapper {
  max-height: 500px;
  overflow-y: auto;
  overflow-x: auto;
  width: 100%;
  -webkit-overflow-scrolling: touch;
}

.dialog-table-wrapper::-webkit-scrollbar {
  height: 6px;
  width: 8px;
}

.dialog-table-wrapper::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

.dialog-table-wrapper::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.dialog-table-wrapper::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Dialog Table Columns */
.dialog-id-col {
  min-width: 60px;
  width: 80px;
  white-space: nowrap;
}

.dialog-company-col {
  min-width: 150px;
  max-width: 300px;
  word-wrap: break-word;
  word-break: break-word;
  white-space: normal;
}

.dialog-date-col {
  min-width: 120px;
  width: 140px;
  white-space: nowrap;
}

.dialog-status-col {
  min-width: 100px;
  width: 120px;
}

/* Table Responsive */
@media (max-width: 600px) {
  .v-simple-table th,
  .v-simple-table td {
    font-size: 0.7rem;
    padding: 6px 3px !important;
  }

  .expiring-company-col,
  .registered-company-col {
    min-width: 100px;
    max-width: 140px;
    font-size: 0.7rem;
  }

  .expiring-id-col,
  .registered-id-col {
    min-width: 40px;
    width: 45px;
  }

  .expiring-date-col,
  .registered-date-col {
    min-width: 85px;
    font-size: 0.65rem;
  }

  .expiring-status-col {
    min-width: 80px;
  }

  .status-badge {
    padding: 3px 6px;
    font-size: 0.7rem;
  }

  .progress {
    font-size: 12px;
  }

  .bar {
    margin: 0 8px;
    height: 14px;
  }
}

/* Medium screens (tablets) */
@media (max-width: 960px) and (min-width: 601px) {
  .v-simple-table th,
  .v-simple-table td {
    font-size: 0.8rem;
    padding: 8px 6px !important;
  }

  .expiring-company-col,
  .registered-company-col {
    max-width: 150px;
  }

  .status-badge {
    font-size: 0.75rem;
  }
}

/* Chart Responsiveness */
@media (max-width: 600px) {
  canvas {
    max-width: 100%;
    height: auto !important;
  }

  .donut-canvas-large {
    max-width: 160px;
    max-height: 160px;
  }
}
</style>
